import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDisplayStore } from '../../context/DisplayStoreProvider'
import { fetchMarkets, formatPct, formatPrice } from '../../lib/lighterApi'

export default function MarketSelectorSheet() {
  const { isMarketSelectorOpen, closeMarketSelector, setSelectedMarket } =
    useDisplayStore()
  const { symbol: routeSymbol } = useParams()
  const navigate = useNavigate()
  const active = (routeSymbol || 'LIT').toUpperCase()
  const [query, setQuery] = useState('')
  const [markets, setMarkets] = useState([])
  const [status, setStatus] = useState('Loading…')

  useEffect(() => {
    if (!isMarketSelectorOpen) return undefined
    let cancelled = false
    setStatus('Loading…')
    fetchMarkets()
      .then((list) => {
        if (cancelled) return
        setMarkets(list)
        setStatus(list.length ? '' : 'No markets')
      })
      .catch(() => {
        if (!cancelled) setStatus('Unable to load markets')
      })
    return () => {
      cancelled = true
    }
  }, [isMarketSelectorOpen])

  useEffect(() => {
    if (isMarketSelectorOpen) return undefined
    setQuery('')
    return undefined
  }, [isMarketSelectorOpen])

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase()
    if (!q) return markets
    return markets.filter((m) => m.symbol.includes(q))
  }, [markets, query])

  if (!isMarketSelectorOpen) return null

  const pick = (symbol) => {
    setSelectedMarket(symbol)
    closeMarketSelector()
    if (symbol !== active) navigate(`/trade/${symbol}`)
  }

  return (
    <div className="trade-market-sheet" role="dialog" aria-modal="true" aria-label="Select market">
      <button
        type="button"
        className="trade-market-sheet__backdrop"
        aria-label="Close market selector"
        onClick={closeMarketSelector}
      />
      <div className="trade-market-sheet__panel">
        <div className="trade-market-sheet__handle" aria-hidden="true" />
        <div className="trade-market-sheet__top">
          <p className="trade-market-sheet__title">Markets</p>
          <button type="button" className="trade-market-sheet__close" onClick={closeMarketSelector}>
            Close
          </button>
        </div>
        <input
          className="trade-market-sheet__search"
          type="search"
          placeholder="Search markets"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="trade-market-sheet__list">
          {status ? (
            <p className="trade-market-sheet__status">{status}</p>
          ) : (
            filtered.map((m) => {
              const change = Number(m.change)
              const up = Number.isFinite(change) ? change >= 0 : true
              return (
                <button
                  key={m.symbol}
                  type="button"
                  className={`trade-market-sheet__row${m.symbol === active ? ' is-active' : ''}`}
                  onClick={() => pick(m.symbol)}
                >
                  <span className="trade-market-sheet__sym">
                    {m.symbol}
                    <span className="trade-market-sheet__lev">{m.leverage}x</span>
                  </span>
                  <span className="trade-market-sheet__px">
                    <span>{m.lastPrice != null ? formatPrice(m.lastPrice, 4) : '—'}</span>
                    {Number.isFinite(change) ? (
                      <span className={up ? 'text-up' : 'text-down'}>{formatPct(change, 2)}</span>
                    ) : null}
                  </span>
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
